import { X } from "lucide-react";
import { useEffect, useRef, type ReactNode } from "react";

import { Button } from "./button.js";
import { cn } from "./cn.js";

/**
 * A dialog, on the element the platform provides.
 *
 * `<dialog showModal>` gives focus containment, Escape, an inert page behind it and a
 * real top layer with no library and no portal. That is the whole reason the reference
 * design system reached for a dialog primitive, and in 2026 the platform answers it —
 * so this app carries a component instead of a dependency.
 */
export function Modal({
  children,
  description,
  onClose,
  open,
  title,
  width = "32rem",
}: {
  children: ReactNode;
  description?: string;
  onClose: () => void;
  open: boolean;
  title: string;
  width?: string;
}) {
  const ref = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (open && !element.open) element.showModal();
    if (!open && element.open) element.close();
  }, [open]);

  return (
    <dialog
      className={cn(
        "m-auto max-h-[calc(100svh-2rem)] w-[calc(100vw-2rem)] overflow-visible rounded-md border border-border bg-surface p-0 text-ink shadow-[var(--shadow)]",
        "backdrop:bg-[oklch(0_0_0/0.55)] backdrop:backdrop-blur-[2px]",
      )}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        // A click that lands on the dialog element itself landed on the backdrop:
        // everything inside is a child of the panel below.
        if (event.target === ref.current) onClose();
      }}
      ref={ref}
      style={{ maxWidth: width }}
    >
      <div className="flex max-h-[calc(100svh-2rem)] flex-col">
        <header className="flex items-start gap-4 border-b border-border px-5 py-4">
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-ink">{title}</h2>
            {description ? (
              <p className="mt-1 text-xs leading-5 text-ink-3">{description}</p>
            ) : null}
          </div>
          <Button
            aria-label="Close"
            onClick={onClose}
            size="icon-small"
            variant="ghost"
          >
            <X aria-hidden="true" />
          </Button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          {children}
        </div>
      </div>
    </dialog>
  );
}
